import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Eye, Heart, MapPin, MoreVertical, Pencil, Trash2, Zap } from "lucide-react";
import { Link, NavLink } from "react-router-dom";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { useSupabaseSession } from "@/hooks/use-supabase-session";
import { useToast } from "@/hooks/use-toast";
import { computeKarmaNumerology, parseBirthDate } from "@/lib/karma/numerology";
import { cn } from "@/lib/utils";
import {
  addSocialPostComment,
  deleteSocialActivity,
  deleteSocialPost,
  deleteSocialPostComment,
  getMySocialHeaderInDb,
  listProfilePostsByTab,
  listRecentSocialActivitiesInDb,
  listSocialConnectionsInDb,
  toggleLikeSocialPost,
  toggleSaveSocialPost,
  updateSocialPost,
  updateSocialPostComment,
  type SocialActivityItem,
  type SocialActivityKind,
  type SocialFeedPost,
  type SocialMediaItem,
} from "@/lib/supabase/social";
import { resilientFetch } from "@/lib/supabase/client";

function initials(name: string) {
  return String(name ?? "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toLocaleUpperCase("tr-TR") ?? "")
    .join("") || "?";
}

function formatRelative(iso: string) {
  const date = new Date(iso);
  if (!Number.isFinite(date.getTime())) return "az önce";
  const diff = Date.now() - date.getTime();
  const minutes = Math.max(0, Math.floor(diff / 60_000));
  if (minutes < 1) return "az önce";
  if (minutes < 60) return `${minutes} dk önce`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} sa önce`;
  const days = Math.floor(hours / 24);
  return `${days} gün önce`;
}

function normalizeTag(value: string) {
  return String(value ?? "")
    .trim()
    .toLocaleLowerCase("tr-TR")
    .replace(/\s+/g, "")
    .replace(/[ç]/g, "c")
    .replace(/[ğ]/g, "g")
    .replace(/[ı]/g, "i")
    .replace(/[ö]/g, "o")
    .replace(/[ş]/g, "s")
    .replace(/[ü]/g, "u")
    .replace(/[^0-9a-z:_-]+/g, "");
}

function normalizeText(value: string) {
  return String(value ?? "")
    .toLocaleLowerCase("tr-TR")
    .replace(/[ç]/g, "c")
    .replace(/[ğ]/g, "g")
    .replace(/[ı]/g, "i")
    .replace(/[ö]/g, "o")
    .replace(/[ş]/g, "s")
    .replace(/[ü]/g, "u")
    .replace(/[^0-9a-z\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function parsePracticeCompletionPost(body: string): { kind: "meditasyon" | "nefes"; title: string } | null {
  const raw = String(body ?? "").trim();
  if (!raw) return null;

  const breathMatch = raw.match(/^Bugünkü\s+nefes\s+seansımı\s+tamamladım:\s*(.+)$/i);
  if (breathMatch?.[1]) {
    return { kind: "nefes", title: breathMatch[1].trim() };
  }

  const meditationMatch = raw.match(/^Bugünkü\s+meditasyonumu\s+tamamladım:\s*(.+)$/i);
  if (meditationMatch?.[1]) {
    return { kind: "meditasyon", title: meditationMatch[1].trim() };
  }

  return null;
}

function parseActivityCompletionPost(body: string): { kind: "aktivite" | "rituel"; title: string } | null {
  const raw = String(body ?? "").trim();
  if (!raw) return null;

  const activityMatch = raw.match(/^Bugünkü\s+aktivitemi\s+tamamladım:\s*(.+)$/i);
  if (activityMatch?.[1]) {
    return { kind: "aktivite", title: activityMatch[1].trim() };
  }

  const ritualMatch = raw.match(/^Bugünkü\s+ritüelimi\s+tamamladım:\s*(.+)$/i);
  if (ritualMatch?.[1]) {
    return { kind: "rituel", title: ritualMatch[1].trim() };
  }

  return null;
}

function formatSessionDuration(durationMinutes?: number) {
  const minutes = Number(durationMinutes);
  if (!Number.isFinite(minutes) || minutes <= 0) return "—";

  const totalSeconds = Math.max(0, Math.round(minutes * 60));
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;

  if (secs === 0) return `${mins} dk`;
  return `${mins}:${String(secs).padStart(2, "0")}`;
}

function parseDurationMinutesFromText(value?: string) {
  const raw = String(value ?? "").trim();
  if (!raw) return undefined;

  const match = raw.match(/(\d+(?:[.,]\d+)?)/);
  if (!match?.[1]) return undefined;

  const parsed = Number(match[1].replace(",", "."));
  if (!Number.isFinite(parsed) || parsed <= 0) return undefined;
  return parsed;
}

function resolveCardDurationMinutes(card?: { durationMinutes?: number; metaValue?: string; metaLabel?: string } | null) {
  if (!card) return undefined;

  const direct = Number(card.durationMinutes);
  if (Number.isFinite(direct) && direct > 0) return direct;

  return parseDurationMinutesFromText(card.metaValue) ?? parseDurationMinutesFromText(card.metaLabel);
}

type CompletionVisualCard = {
  title: string;
  coverImageUrl?: string;
  durationMinutes?: number;
  metaValue?: string;
  metaLabel?: string;
};

function formatFocusPercent(value?: number | null) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return "%0";
  return `%${Math.max(0, Math.min(100, Math.round(parsed)))}`;
}

function resolveCompletionVisuals(args: {
  title: string;
  primaryCards: CompletionVisualCard[];
  fallbackCards?: CompletionVisualCard[];
}) {
  const allCards = [...(args.primaryCards ?? []), ...(args.fallbackCards ?? [])];
  const normalizedTitle = normalizeText(args.title);

  const exactMatch = allCards.find((card) => normalizeText(card.title) === normalizedTitle) ?? null;
  const partialMatch =
    allCards.find((card) => {
      const cardTitle = normalizeText(card.title);
      return cardTitle.length >= 3 && normalizedTitle.length >= 3 && (normalizedTitle.includes(cardTitle) || cardTitle.includes(normalizedTitle));
    }) ?? null;

  const matchedCard = exactMatch ?? partialMatch;
  const durationCard = matchedCard ?? allCards.find((card) => resolveCardDurationMinutes(card) !== undefined) ?? null;
  const coverCard = (matchedCard?.coverImageUrl ? matchedCard : null) ?? allCards.find((card) => Boolean(card.coverImageUrl)) ?? null;

  return {
    matchedCard,
    durationMinutes: resolveCardDurationMinutes(durationCard),
    coverImageUrl: coverCard?.coverImageUrl || DEFAULT_COMPLETION_COVER_IMAGE,
  };
}

function resolveCompletionStreakDays(args: {
  activities: SocialActivityItem[];
  authorUserId: string;
  title: string;
  kind: "meditasyon" | "nefes" | "aktivite" | "rituel";
  createdAt: string;
}) {
  const { activities, authorUserId, title, kind, createdAt } = args;
  const normalizedTitle = normalizeText(title);
  const targetTime = new Date(createdAt).getTime();

  const kindMatches = (taskKind: SocialActivityKind) => {
    if (kind === "rituel") return taskKind === "rituel";
    if (kind === "nefes") return taskKind === "nefes";
    if (kind === "meditasyon") return taskKind === "meditasyon";
    return taskKind !== "rituel";
  };

  const candidates = activities.filter((activity) => {
    if (activity.authorUserId !== authorUserId) return false;
    if (!kindMatches(activity.taskKind)) return false;

    const activityTitle = normalizeText(activity.title);
    if (!activityTitle || !normalizedTitle) return false;
    return normalizedTitle.includes(activityTitle) || activityTitle.includes(normalizedTitle);
  });

  if (!candidates.length) return 1;

  const best = candidates
    .slice()
    .sort((a, b) => {
      const aDiff = Math.abs(new Date(a.createdAt).getTime() - targetTime);
      const bDiff = Math.abs(new Date(b.createdAt).getTime() - targetTime);
      return aDiff - bDiff;
    })[0];

  return Math.max(1, Math.round(Number(best?.streakDays) || 1));
}

function formatLocalDateKey(d: Date) {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseProgramDaysFromTags(tags: string[]) {
  const raw = (tags ?? []).find((tag) => String(tag ?? "").startsWith("program_gun:"))?.slice("program_gun:".length) ?? "";
  const value = Number(raw);
  return Number.isFinite(value) && value > 0 ? Math.round(value) : 0;
}

function parseRitualProgramDaysFromTags(tags: string[]) {
  const raw = (tags ?? []).find((tag) => String(tag ?? "").startsWith("rituel_program_gun:"))?.slice("rituel_program_gun:".length) ?? "";
  const value = Number(raw);
  return Number.isFinite(value) && value > 0 ? Math.round(value) : 0;
}

type ProgramSeriesItem = {
  id: string;
  tags: string[];
  iconKey?: string;
};

type MeditationSession = {
  id: string;
  title: string;
  durationMinutes: number;
  coverImageUrl?: string;
  active: boolean;
  tags?: string[];
};

type DietPlanCard = {
  id: string;
  title: string;
  coverImageUrl?: string;
  active: boolean;
};

type WellnessActivityCard = {
  id: string;
  title: string;
  durationMinutes?: number;
  metaLabel?: string;
  metaValue?: string;
  coverImageUrl?: string;
  active: boolean;
};

async function fetchJsonItems<T>(url: string): Promise<T[]> {
  try {
    const res = await resilientFetch(url);
    const json = (await res.json().catch(() => null)) as { items?: T[] } | null;
    if (!res.ok) return [];
    return Array.isArray(json?.items) ? json.items : [];
  } catch {
    return [];
  }
}

async function fetchMeditationSeriesItems(): Promise<ProgramSeriesItem[]> {
  return fetchJsonItems<ProgramSeriesItem>("/api/numeroloji/meditations");
}

async function fetchMeditationCards(): Promise<MeditationSession[]> {
  const items = await fetchJsonItems<MeditationSession>("/api/numeroloji/meditations");
  return items.filter((item) => item?.active !== false);
}

async function fetchBreathCards(): Promise<MeditationSession[]> {
  const items = await fetchJsonItems<MeditationSession>("/api/numeroloji/breaths");
  return items.filter((item) => item?.active !== false);
}

async function fetchDietSeriesItems(): Promise<ProgramSeriesItem[]> {
  return fetchJsonItems<ProgramSeriesItem>("/api/numeroloji/diets");
}

async function fetchActivitySeriesItems(): Promise<ProgramSeriesItem[]> {
  return fetchJsonItems<ProgramSeriesItem>("/api/numeroloji/activities");
}

async function fetchDietCards(): Promise<DietPlanCard[]> {
  const items = await fetchJsonItems<DietPlanCard>("/api/numeroloji/diets");
  return items.filter((item) => item?.active !== false);
}

async function fetchRitualCards(): Promise<WellnessActivityCard[]> {
  const items = await fetchJsonItems<WellnessActivityCard>("/api/numeroloji/activities");
  return items.filter((item) => item?.active !== false);
}

function MediaCarousel(props: { media: SocialMediaItem[]; mediaIndex: number; onChange: (next: number) => void }) {
  const { media, mediaIndex, onChange } = props;
  if (!media.length) return null;

  const current = media[Math.max(0, Math.min(media.length - 1, mediaIndex))];

  return (
    <div className="relative overflow-hidden rounded-xl border border-slate-200 bg-slate-100 dark:border-slate-700 dark:bg-slate-900">
      <div className="aspect-[4/5] w-full">
        {current.type === "video" ? (
          <video src={current.url} controls className="h-full w-full object-cover" />
        ) : (
          <img src={current.url} alt="Paylaşım medyası" className="h-full w-full object-cover" />
        )}
      </div>

      {media.length > 1 ? (
        <>
          <button
            type="button"
            className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/45 px-2 py-1 text-xs text-white"
            onClick={() => onChange((mediaIndex - 1 + media.length) % media.length)}
          >
            ◀
          </button>
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/45 px-2 py-1 text-xs text-white"
            onClick={() => onChange((mediaIndex + 1) % media.length)}
          >
            ▶
          </button>
          <div className="absolute bottom-2 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-full bg-black/45 px-2 py-1">
            {media.map((item, index) => (
              <button
                key={item.id}
                type="button"
                className={cn("h-1.5 w-1.5 rounded-full", mediaIndex === index ? "bg-white" : "bg-white/40")}
                onClick={() => onChange(index)}
                aria-label={`Medya ${index + 1}`}
              />
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

const PROGRAM_DAY_TRACKING_KEY = "numeroloji_program_day_tracking_v1";
const RITUAL_SERIES_TRACKS = ["meditation", "breath", "nutrition", "ritual"] as const;

type RitualSeriesTrack = (typeof RITUAL_SERIES_TRACKS)[number];

type ProgramDayTrackingState = {
  startedAtByItemId: Record<string, string>;
};

type ProgramDayTrackingStore = Record<string, ProgramDayTrackingState>;
type ActivityInteractionState = Record<string, { sentEnergy: boolean; sentHealing: boolean }>;

const ACTIVITY_INTERACTIONS_KEY = "panel_activity_interactions_v1";
const ACTIVITY_VIEWED_KEY = "panel_activity_viewed_v1";

const ENERGY_BUTTON_CLASS = "rounded-full border-transparent bg-[var(--panel-primary)] text-white hover:brightness-95 disabled:opacity-100";
const HEALING_BUTTON_CLASS = "rounded-full border-transparent bg-[var(--panel-primary)] text-white hover:brightness-95 disabled:opacity-100";
const DEFAULT_COMPLETION_COVER_IMAGE = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 700" preserveAspectRatio="xMidYMid slice">
  <defs>
    <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="#f8e8ff"/>
      <stop offset="50%" stop-color="#d8b4fe"/>
      <stop offset="100%" stop-color="#a78bfa"/>
    </linearGradient>
    <radialGradient id="glow1" cx="20%" cy="25%" r="40%">
      <stop offset="0%" stop-color="#ffffff" stop-opacity="0.75"/>
      <stop offset="100%" stop-color="#ffffff" stop-opacity="0"/>
    </radialGradient>
    <radialGradient id="glow2" cx="80%" cy="75%" r="45%">
      <stop offset="0%" stop-color="#fdf4ff" stop-opacity="0.65"/>
      <stop offset="100%" stop-color="#fdf4ff" stop-opacity="0"/>
    </radialGradient>
  </defs>
  <rect width="1200" height="700" fill="url(#bg)"/>
  <circle cx="220" cy="180" r="230" fill="url(#glow1)"/>
  <circle cx="980" cy="540" r="270" fill="url(#glow2)"/>
</svg>
`)}`;

function applyCoverFallbackImage(target: HTMLImageElement) {
  if (!target) return;
  if (target.dataset.coverFallbackApplied === "1") return;
  target.dataset.coverFallbackApplied = "1";
  target.src = DEFAULT_COMPLETION_COVER_IMAGE;
}

function readActivityInteractionsStore(): ActivityInteractionState {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(ACTIVITY_INTERACTIONS_KEY) ?? "";
    if (!raw) return {};
    const parsed = JSON.parse(raw) as ActivityInteractionState;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function readViewedActivityStore(): Record<string, true> {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(ACTIVITY_VIEWED_KEY) ?? "";
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, true>;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function saveActivityInteractionsStore(store: ActivityInteractionState) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(ACTIVITY_INTERACTIONS_KEY, JSON.stringify(store));
  } catch {
    // ignore
  }
}

export default function UserPanelSosyalProfil() {
  const { user, loading } = useSupabaseSession();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [menuPostId, setMenuPostId] = useState<string | null>(null);
  const [menuActivityId, setMenuActivityId] = useState<string | null>(null);
  const [editingPostId, setEditingPostId] = useState<string | null>(null);
  const [editingBody, setEditingBody] = useState("");
  const [commentDraftByPostId, setCommentDraftByPostId] = useState<Record<string, string>>({});
  const [commentVisibleCountByPostId, setCommentVisibleCountByPostId] = useState<Record<string, number>>({});
  const [commentEditBodyById, setCommentEditBodyById] = useState<Record<string, string>>({});

  const headerQuery = useQuery({
    queryKey: ["social-hub-header", user?.id],
    enabled: Boolean(user?.id) && !loading,
    queryFn: () => getMySocialHeaderInDb(),
    staleTime: 30_000,
  });

  const connectionsQuery = useQuery({
    queryKey: ["social-hub-connections", user?.id],
    enabled: Boolean(user?.id) && !loading,
    queryFn: () => listSocialConnectionsInDb(),
    staleTime: 10_000,
  });

  const tabPostsQuery = useQuery({
    queryKey: ["social-hub-posts", user?.id],
    enabled: Boolean(user?.id) && !loading,
    queryFn: () => listProfilePostsByTab({ tab: "posts", limit: 80 }),
    staleTime: 5_000,
  });

  const activityCompletionsQuery = useQuery({
    queryKey: ["social-profile-activities", user?.id],
    enabled: Boolean(user?.id) && !loading,
    queryFn: () => listRecentSocialActivitiesInDb(20),
    staleTime: 5_000,
  });

  const meditationCardsQuery = useQuery({
    queryKey: ["social-profile-activity-meditations", user?.id],
    enabled: Boolean(user?.id) && !loading,
    queryFn: fetchMeditationCards,
    staleTime: 60_000,
  });

  const breathCardsQuery = useQuery({
    queryKey: ["social-profile-activity-breaths", user?.id],
    enabled: Boolean(user?.id) && !loading,
    queryFn: fetchBreathCards,
    staleTime: 60_000,
  });

  const dietCardsQuery = useQuery({
    queryKey: ["social-profile-activity-diets", user?.id],
    enabled: Boolean(user?.id) && !loading,
    queryFn: fetchDietCards,
    staleTime: 60_000,
  });

  const ritualCardsQuery = useQuery({
    queryKey: ["social-profile-activity-rituals", user?.id],
    enabled: Boolean(user?.id) && !loading,
    queryFn: fetchRitualCards,
    staleTime: 60_000,
  });

  const meditationSeriesQuery = useQuery({
    queryKey: ["ritual-series-meditations", user?.id],
    enabled: Boolean(user?.id) && !loading,
    queryFn: fetchMeditationSeriesItems,
    staleTime: 60_000,
  });

  const dietSeriesQuery = useQuery({
    queryKey: ["ritual-series-diets", user?.id],
    enabled: Boolean(user?.id) && !loading,
    queryFn: fetchDietSeriesItems,
    staleTime: 60_000,
  });

  const activitySeriesQuery = useQuery({
    queryKey: ["ritual-series-activities", user?.id],
    enabled: Boolean(user?.id) && !loading,
    queryFn: fetchActivitySeriesItems,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!user?.id || loading) return;

    void Promise.all([
      queryClient.invalidateQueries({ queryKey: ["social-hub-header", user.id] }),
      queryClient.invalidateQueries({ queryKey: ["social-hub-connections", user.id] }),
      queryClient.invalidateQueries({ queryKey: ["social-hub-posts", user.id] }),
      queryClient.invalidateQueries({ queryKey: ["social-profile-activities", user.id] }),
    ]);

    void headerQuery.refetch();
  }, [headerQuery, loading, queryClient, user?.id]);

  const commonInvalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["social-hub-posts", user?.id] }),
      queryClient.invalidateQueries({ queryKey: ["social-feed-posts", user?.id] }),
      queryClient.invalidateQueries({ queryKey: ["social-hub-connections", user?.id] }),
      queryClient.invalidateQueries({ queryKey: ["social-profile-activities", user?.id] }),
      queryClient.invalidateQueries({ queryKey: ["social-feed-activities", user?.id] }),
    ]);
  };

  const editPostMutation = useMutation({
    mutationFn: (args: { postId: string; body: string }) => updateSocialPost(args),
    onSuccess: async () => {
      setEditingPostId(null);
      setEditingBody("");
      setMenuPostId(null);
      toast({ title: "Paylaşım güncellendi" });
      await commonInvalidate();
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Paylaşım güncellenemedi";
      toast({ title: "Hata", description: message, variant: "destructive" });
    },
  });

  const deletePostMutation = useMutation({
    mutationFn: (postId: string) => deleteSocialPost(postId),
    onSuccess: async () => {
      setMenuPostId(null);
      toast({ title: "Paylaşım silindi" });
      await commonInvalidate();
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Paylaşım silinemedi";
      toast({ title: "Hata", description: message, variant: "destructive" });
    },
  });

  const deleteActivityMutation = useMutation({
    mutationFn: (activityId: string) => deleteSocialActivity(activityId),
    onSuccess: async () => {
      setMenuActivityId(null);
      toast({ title: "Aktivite paylaşımı silindi" });
      await commonInvalidate();
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Aktivite silinemedi";
      toast({ title: "Hata", description: message, variant: "destructive" });
    },
  });

  const addCommentMutation = useMutation({
    mutationFn: (args: { postId: string; body: string }) => addSocialPostComment(args),
    onSuccess: async (_, variables) => {
      setCommentDraftByPostId((prev) => ({ ...prev, [variables.postId]: "" }));
      await commonInvalidate();
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Yorum gönderilemedi";
      toast({ title: "Hata", description: message, variant: "destructive" });
    },
  });

  const updateCommentMutation = useMutation({
    mutationFn: (args: { postId: string; commentId: string; body: string }) => updateSocialPostComment(args),
    onSuccess: async (_, variables) => {
      setCommentEditBodyById((prev) => {
        const next = { ...prev };
        delete next[variables.commentId];
        return next;
      });
      await commonInvalidate();
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Yorum güncellenemedi";
      toast({ title: "Hata", description: message, variant: "destructive" });
    },
  });

  const deleteCommentMutation = useMutation({
    mutationFn: (args: { postId: string; commentId: string }) => deleteSocialPostComment(args),
    onSuccess: async (_, variables) => {
      setCommentEditBodyById((prev) => {
        const next = { ...prev };
        delete next[variables.commentId];
        return next;
      });
      await commonInvalidate();
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Yorum silinemedi";
      toast({ title: "Hata", description: message, variant: "destructive" });
    },
  });

  const applyOptimisticPostToggle = (postId: string, field: "likedByMe" | "savedByMe") => {
    let nextValue = false;

    queryClient.setQueryData<SocialFeedPost[] | undefined>(["social-hub-posts", user?.id], (prev) => {
      if (!Array.isArray(prev)) return prev;

      return prev.map((item) => {
        if (item.id !== postId) return item;

        const currentValue = Boolean(item[field]);
        nextValue = !currentValue;
        const delta = nextValue ? 1 : -1;

        if (field === "likedByMe") {
          return {
            ...item,
            likedByMe: nextValue,
            likeCount: Math.max(0, item.likeCount + delta),
            interactionCount: Math.max(0, item.interactionCount + delta),
          } satisfies SocialFeedPost;
        }

        return {
          ...item,
          savedByMe: nextValue,
          saveCount: Math.max(0, item.saveCount + delta),
          interactionCount: Math.max(0, item.interactionCount + delta),
        } satisfies SocialFeedPost;
      });
    });

    return nextValue;
  };

  const handlePostInteractionClick = async (postId: string, action: "energy" | "healing") => {
    applyOptimisticPostToggle(postId, action === "energy" ? "likedByMe" : "savedByMe");

    try {
      if (action === "energy") {
        await toggleLikeSocialPost(postId);
      } else {
        await toggleSaveSocialPost(postId);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Etkileşim güncellenemedi";
      toast({ title: "Hata", description: message, variant: "destructive" });
    } finally {
      void commonInvalidate();
    }
  };

  const submitCommentForPost = (postId: string) => {
    const body = String(commentDraftByPostId[postId] ?? "").trim();
    if (!body) return;
    void addCommentMutation.mutate({ postId, body });
  };

  const startEditComment = (commentId: string, body: string) => {
    setCommentEditBodyById((prev) => ({ ...prev, [commentId]: body }));
  };

  const cancelEditComment = (commentId: string) => {
    setCommentEditBodyById((prev) => {
      const next = { ...prev };
      delete next[commentId];
      return next;
    });
  };

  const saveEditedComment = (postId: string, commentId: string) => {
    const body = String(commentEditBodyById[commentId] ?? "").trim();
    if (!body) return;
    void updateCommentMutation.mutate({ postId, commentId, body });
  };

  const showMoreComments = (postId: string) => {
    setCommentVisibleCountByPostId((prev) => ({
      ...prev,
      [postId]: (prev[postId] ?? 10) + 10,
    }));
  };

  const renderPostComments = (post: SocialFeedPost, variant: "default" | "dark" = "default") => {
    const draft = commentDraftByPostId[post.id] ?? "";
    const comments = post.comments ?? [];
    const visibleCount = commentVisibleCountByPostId[post.id] ?? 10;
    const visibleComments = comments.slice(0, visibleCount);
    const hasMore = comments.length > visibleCount;
    const isPendingForPost = addCommentMutation.isPending && addCommentMutation.variables?.postId === post.id;

    return (
      <div className="space-y-2">
        <div className={cn("text-xs", variant === "dark" ? "text-white/85" : "text-slate-500")}>{post.commentCount} yorum</div>

        <div className="flex items-start gap-2">
          <Textarea
            value={draft}
            onChange={(event) => setCommentDraftByPostId((prev) => ({ ...prev, [post.id]: event.target.value }))}
            maxLength={300}
            placeholder="Yorum yaz..."
            className={cn(
              "min-h-[40px] resize-none text-sm",
              variant === "dark"
                ? "border-white/30 bg-white/20 text-white placeholder:text-white/70"
                : "border-[#ddc6df] bg-white text-slate-700 placeholder:text-slate-400",
            )}
          />
          <Button
            type="button"
            size="sm"
            className="mt-1"
            disabled={isPendingForPost || !draft.trim()}
            onClick={() => submitCommentForPost(post.id)}
          >
            Gönder
          </Button>
        </div>

        {visibleComments.length ? (
          <div className="space-y-1">
            {visibleComments.map((comment) => {
              const canManage = comment.userId === user?.id;
              const editingValue = commentEditBodyById[comment.id];
              const isEditing = typeof editingValue === "string";
              const isUpdating =
                updateCommentMutation.isPending &&
                updateCommentMutation.variables?.postId === post.id &&
                updateCommentMutation.variables?.commentId === comment.id;
              const isDeleting =
                deleteCommentMutation.isPending &&
                deleteCommentMutation.variables?.postId === post.id &&
                deleteCommentMutation.variables?.commentId === comment.id;

              return (
                <div
                  key={comment.id}
                  className={cn(
                    "rounded-lg px-2 py-1.5 text-xs",
                    variant === "dark" ? "bg-white/20 text-white" : "bg-[#f4edf6] text-slate-700",
                  )}
                >
                  {isEditing ? (
                    <div className="space-y-2">
                      <Textarea
                        value={editingValue}
                        onChange={(event) => setCommentEditBodyById((prev) => ({ ...prev, [comment.id]: event.target.value }))}
                        maxLength={300}
                        className={cn(
                          "min-h-[44px] resize-none text-xs",
                          variant === "dark"
                            ? "border-white/30 bg-white/20 text-white placeholder:text-white/70"
                            : "border-[#ddc6df] bg-white text-slate-700 placeholder:text-slate-400",
                        )}
                      />
                      <div className="flex items-center justify-end gap-1">
                        <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={() => cancelEditComment(comment.id)}>
                          Vazgeç
                        </Button>
                        <Button type="button" size="sm" className="h-7 px-2 text-[11px]" disabled={isUpdating || !editingValue.trim()} onClick={() => saveEditedComment(post.id, comment.id)}>
                          Kaydet
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <span className="font-semibold">{comment.userName}: </span>
                        <span>{comment.body}</span>
                      </div>
                      {canManage ? (
                        <div className="flex items-center gap-1">
                          <Button type="button" size="icon" variant="ghost" className={cn("h-6 w-6", variant === "dark" && "text-white hover:bg-white/20")} onClick={() => startEditComment(comment.id, comment.body)}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button type="button" size="icon" variant="ghost" className={cn("h-6 w-6 text-red-500 hover:bg-red-50", variant === "dark" && "hover:bg-red-500/20")} disabled={isDeleting} onClick={() => void deleteCommentMutation.mutate({ postId: post.id, commentId: comment.id })}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : null}

        {hasMore ? (
          <button
            type="button"
            onClick={() => showMoreComments(post.id)}
            className={cn("text-xs font-semibold underline underline-offset-2", variant === "dark" ? "text-white/90" : "text-[#7a4a95]")}
          >
            Devamı oku
          </button>
        ) : null}
      </div>
    );
  };

  const fallbackHeader = useMemo(() => {
    const meta = (user?.metadata ?? {}) as Record<string, unknown>;
    const first = typeof meta.first_name === "string" ? meta.first_name.trim() : typeof meta.firstName === "string" ? meta.firstName.trim() : "";
    const last = typeof meta.last_name === "string" ? meta.last_name.trim() : typeof meta.lastName === "string" ? meta.lastName.trim() : "";
    const full = `${first} ${last}`.trim();
    const fullName = typeof meta.full_name === "string" ? meta.full_name.trim() : typeof meta.fullName === "string" ? meta.fullName.trim() : "";
    const displayName = typeof meta.display_name === "string" ? meta.display_name.trim() : typeof meta.displayName === "string" ? meta.displayName.trim() : "";
    const handle =
      (typeof meta.handle === "string" ? meta.handle.trim() : "") ||
      (typeof user?.email === "string" && user.email.includes("@") ? user.email.split("@")[0].trim() : "") ||
      "user";

    const birthDay = Number(meta.birth_day ?? meta.birthDay ?? 0);
    const birthMonth = Number(meta.birth_month ?? meta.birthMonth ?? 0);
    const birthYear = Number(meta.birth_year ?? meta.birthYear ?? 0);

    let destinyNumber: number | null = null;
    let lifePathNumber: number | null = null;
    let expressionNumber: number | null = null;

    if (birthDay > 0 && birthMonth > 0 && birthYear > 0) {
      try {
        const birthDate = parseBirthDate({ day: birthDay, month: birthMonth, year: birthYear });
        const numerology = computeKarmaNumerology(full || fullName || displayName || handle || "Kullanıcı", birthDate);
        destinyNumber = numerology.destinyNumber;
        lifePathNumber = numerology.lifePathMaster ?? numerology.destinyNumber;
        expressionNumber = numerology.expressionNumber;
      } catch {
        destinyNumber = null;
        lifePathNumber = null;
        expressionNumber = null;
      }
    }

    return {
      id: String(user?.id ?? ""),
      displayName: full || fullName || displayName || "Profil",
      handle,
      photoUrl: null,
      bio: null,
      interests: [] as string[],
      matchEnabled: true,
      destinyNumber,
      lifePathNumber,
      expressionNumber,
    };
  }, [user?.email, user?.id, user?.metadata]);

  const header = {
    ...fallbackHeader,
    ...(headerQuery.data ?? {}),
    lifePathNumber: headerQuery.data?.lifePathNumber ?? fallbackHeader.lifePathNumber,
    expressionNumber: headerQuery.data?.expressionNumber ?? fallbackHeader.expressionNumber,
  };
  const connections = connectionsQuery.data;

  const tabPosts = tabPostsQuery.data ?? [];
  const filteredTabPosts = useMemo(
    () => [...tabPosts].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [tabPosts],
  );
  const activityCompletions = useMemo(
    () =>
      [...(activityCompletionsQuery.data ?? [])]
        .filter((activity) => activity.authorUserId === user?.id)
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [activityCompletionsQuery.data, user?.id],
  );
  const [activityInteractions, setActivityInteractions] = useState<ActivityInteractionState>(() => readActivityInteractionsStore());
  const [viewedActivityIds] = useState<Record<string, true>>(() => readViewedActivityStore());

  const mergedProfileFeed = useMemo(
    () =>
      [
        ...activityCompletions.map((activity) => ({ kind: "activity" as const, id: `activity_${activity.id}`, createdAt: activity.createdAt, activity })),
        ...filteredTabPosts.map((post) => ({ kind: "post" as const, id: `post_${post.id}`, createdAt: post.createdAt, post })),
      ].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [activityCompletions, filteredTabPosts],
  );

  const toggleActivityInteraction = (activityId: string, key: "sentEnergy" | "sentHealing") => {
    setActivityInteractions((prev) => {
      const current = prev[activityId] ?? { sentEnergy: false, sentHealing: false };
      if (current[key]) return prev;

      const next = {
        ...prev,
        [activityId]: {
          ...current,
          [key]: true,
        },
      };
      saveActivityInteractionsStore(next);
      return next;
    });
  };

  const resolveActivityKindMeta = (kind: SocialActivityKind) => {
    if (kind === "nefes") return { completedLabel: "nefes çalışmasını", ctaHref: "/panel/nefes" };
    if (kind === "diyet") return { completedLabel: "diyet planını", ctaHref: "/panel/diyet" };
    if (kind === "rituel") return { completedLabel: "ritüelini", ctaHref: "/panel/rituel" };
    return { completedLabel: "meditasyonunu", ctaHref: "/panel/meditasyon" };
  };

  const resolveMeditationForActivity = (activity: SocialActivityItem) => {
    const meditations = meditationCardsQuery.data ?? [];
    if (!meditations.length) return null;

    const combined = normalizeText(`${activity.title} ${activity.subtitle}`);
    const matched = meditations.find((item) => {
      const normalizedTitle = normalizeText(item.title);
      return normalizedTitle.length >= 4 && combined.includes(normalizedTitle);
    });

    return matched ?? null;
  };

  const resolveBreathForActivity = (activity: SocialActivityItem) => {
    const breaths = breathCardsQuery.data ?? [];
    if (!breaths.length) return null;

    const combined = normalizeText(`${activity.title} ${activity.subtitle}`);
    const matched = breaths.find((item) => {
      const normalizedTitle = normalizeText(item.title);
      return normalizedTitle.length >= 4 && combined.includes(normalizedTitle);
    });

    return matched ?? null;
  };

  const resolveDietForActivity = (activity: SocialActivityItem) => {
    const diets = dietCardsQuery.data ?? [];
    if (!diets.length) return null;
    const combined = normalizeText(`${activity.title} ${activity.subtitle}`);
    const matched = diets.find((item) => {
      const normalizedTitle = normalizeText(item.title);
      return normalizedTitle.length >= 4 && combined.includes(normalizedTitle);
    });
    return matched ?? null;
  };

  const resolveRitualForActivity = (activity: SocialActivityItem) => {
    const rituals = ritualCardsQuery.data ?? [];
    if (!rituals.length) return null;
    const combined = normalizeText(`${activity.title} ${activity.subtitle}`);
    const matched = rituals.find((item) => {
      const normalizedTitle = normalizeText(item.title);
      return normalizedTitle.length >= 4 && combined.includes(normalizedTitle);
    });
    return matched ?? null;
  };

  const ritualSeriesStatus = useMemo<Record<RitualSeriesTrack, boolean>>(() => {
    if (!user?.id || typeof window === "undefined") {
      return { meditation: false, breath: false, nutrition: false, ritual: false };
    }

    const todayKey = formatLocalDateKey(new Date());
    const userKey = `sb:${user.id}`;

    let startedAtByItemId: Record<string, string> = {};
    try {
      const raw = window.localStorage.getItem(PROGRAM_DAY_TRACKING_KEY);
      const parsed = raw ? (JSON.parse(raw) as ProgramDayTrackingStore) : null;
      startedAtByItemId = parsed?.[userKey]?.startedAtByItemId ?? {};
    } catch {
      startedAtByItemId = {};
    }

    const resolveProgramDay = (startedDateKey?: string) => {
      if (!startedDateKey) return 0;
      const started = new Date(`${startedDateKey}T00:00:00`);
      const today = new Date(`${todayKey}T00:00:00`);
      if (!Number.isFinite(started.getTime()) || !Number.isFinite(today.getTime())) return 0;
      const diffDays = Math.floor((today.getTime() - started.getTime()) / 86_400_000);
      return diffDays >= 0 ? diffDays + 1 : 0;
    };

    const hasCompletedSeries = (items: ProgramSeriesItem[], getProgramDays: (tags: string[]) => number, predicate?: (item: ProgramSeriesItem) => boolean) =>
      items.some((item) => {
        if (predicate && !predicate(item)) return false;
        const programDays = getProgramDays(item.tags ?? []);
        if (programDays <= 0) return false;
        return resolveProgramDay(startedAtByItemId[item.id]) >= programDays;
      });

    const meditations = meditationSeriesQuery.data ?? [];
    const diets = dietSeriesQuery.data ?? [];
    const activities = activitySeriesQuery.data ?? [];

    const isBreath = (item: ProgramSeriesItem) => {
      const tags = new Set((item.tags ?? []).map((tag) => normalizeTag(tag)));
      return tags.has("nefes") || tags.has("breath");
    };

    const isMeditationOnly = (item: ProgramSeriesItem) => {
      const tags = new Set((item.tags ?? []).map((tag) => normalizeTag(tag)));
      if (tags.has("nefes") || tags.has("breath")) return false;
      return true;
    };

    const isRitual = (item: ProgramSeriesItem) => {
      const icon = normalizeTag(item.iconKey ?? "");
      const tags = new Set((item.tags ?? []).map((tag) => normalizeTag(tag)));
      return icon === "rituel" || tags.has("rituel") || tags.has("ritual") || (item.tags ?? []).some((tag) => String(tag ?? "").startsWith("rituel_program_gun:"));
    };

    return {
      meditation: hasCompletedSeries(meditations, parseProgramDaysFromTags, isMeditationOnly),
      breath: hasCompletedSeries(meditations, parseProgramDaysFromTags, isBreath),
      nutrition: hasCompletedSeries(diets, parseProgramDaysFromTags),
      ritual: hasCompletedSeries(activities, parseRitualProgramDaysFromTags, isRitual),
    };
  }, [activitySeriesQuery.data, dietSeriesQuery.data, meditationSeriesQuery.data, user?.id]);

  const completedSeriesCount = RITUAL_SERIES_TRACKS.filter((track) => ritualSeriesStatus[track]).length;
  const statCardsLoading = connectionsQuery.isLoading;
  const energyScore = statCardsLoading ? null : Math.min(99, 68 + (connections?.friendCount ?? 0));
  const seriesCount = completedSeriesCount;
  const spiritualBondCount = statCardsLoading ? null : (connections?.followerCount ?? 0) + (connections?.followingCount ?? 0);

  const quickLinks = [
    { to: "/panel/meditasyon", label: "Meditasyon" },
    { to: "/panel/nefes", label: "Nefes" },
    { to: "/panel/diyet", label: "Diyet" },
    { to: "/panel/aktivite", label: "Aktivite" },
    { to: "/panel/rituel", label: "Ritüel" },
    { to: "/panel/gunluk", label: "Rozet Yolculuğu" },
  ];

  if (!user?.id && !loading) {
    return <div className="rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-600">Sosyal profil için giriş yapmalısın.</div>;
  }

  return (
    <div className="space-y-6">
      <section className="rounded-[30px] border border-[#d7b6d8] bg-gradient-to-br from-[#fff7fc] via-[#f7e9f7] to-[#f2e0f1] p-5 shadow-sm">
        <div className="mb-5 flex justify-end">
          <div className="flex flex-wrap items-center justify-end gap-2">
            {quickLinks.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    "rounded-xl border px-3 py-2 text-xs font-semibold transition-colors sm:text-sm",
                    isActive
                      ? "border-[#8f78e5] bg-[#8f78e5] text-white"
                      : "border-[#d8bad7] bg-[#f8edf7] text-[#3a2745] hover:bg-[#f1e5f1]",
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </div>
        </div>

        <div className="grid items-start gap-5 lg:grid-cols-[1fr_auto]">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <Avatar className="h-24 w-24 border-4 border-white shadow-lg">
              <AvatarImage src={header?.photoUrl ?? ""} alt={header?.displayName ?? "Profil"} />
              <AvatarFallback className="text-xl font-bold">{initials(header?.displayName ?? "Kullanıcı")}</AvatarFallback>
            </Avatar>

            <div className="min-w-0">
              <h1 className="truncate text-3xl font-black tracking-tight text-[#2f1f3c]">{header?.displayName ?? "Profil"}</h1>
              <p className="mt-1 text-sm font-medium text-[#6f5a77]">@{header?.handle ?? "user"}</p>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                <Badge className="bg-primary/10 text-primary hover:bg-primary/10">Yaşam Yolu: {header?.lifePathNumber ?? "-"}</Badge>
                <Badge variant="secondary">İfade Numarası: {header?.expressionNumber ?? "-"}</Badge>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <Button type="button" className="rounded-xl">Mesaj Gönder</Button>
                <Button type="button" variant="outline" className="rounded-xl">
                  Takip Et
                </Button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 rounded-2xl border border-[#d8bad7] bg-white/95 p-4 text-center">
            <div>
              <div className="text-2xl font-black text-primary">{energyScore === null ? "—" : `${energyScore}%`}</div>
              <div className="mt-1 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Enerji Seviyesi</div>
            </div>
            <Link to="/panel/profil/rituel-serisi" className="rounded-xl px-1 py-1 transition-colors hover:bg-[#f6edf8] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40">
              <div className="text-2xl font-black text-primary">{seriesCount === null ? "—" : seriesCount}</div>
              <div className="mt-1 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Seri Özeti</div>
            </Link>
            <div>
              <div className="text-2xl font-black text-primary">{spiritualBondCount === null ? "—" : spiritualBondCount}</div>
              <div className="mt-1 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Ruhsal Bağ</div>
            </div>
          </div>
        </div>
      </section>

      <div>
        <section className="space-y-6">
          <div className="space-y-4">
            {mergedProfileFeed.length ? (
              mergedProfileFeed.map((item) => {
                if (item.kind === "activity") {
                  const activity = item.activity;
                  const activityMeta = resolveActivityKindMeta(activity.taskKind);
                  const meditation = activity.taskKind === "meditasyon" ? resolveMeditationForActivity(activity) : null;
                  const breath = activity.taskKind === "nefes" ? resolveBreathForActivity(activity) : null;
                  const dietPlan = activity.taskKind === "diyet" ? resolveDietForActivity(activity) : null;
                  const ritual = activity.taskKind === "rituel" ? resolveRitualForActivity(activity) : null;
                  const activityDisplayTitle = dietPlan?.title ?? ritual?.title ?? breath?.title ?? meditation?.title ?? activity.title;
                  const activityDuration = breath?.durationMinutes ?? meditation?.durationMinutes ?? ritual?.durationMinutes ?? 25;
                  const fallbackBackgroundImageUrl =
                    activity.taskKind === "diyet"
                      ? (dietCardsQuery.data ?? [])[0]?.coverImageUrl ?? null
                      : activity.taskKind === "rituel"
                        ? (ritualCardsQuery.data ?? [])[0]?.coverImageUrl ?? null
                        : activity.taskKind === "nefes"
                          ? (breathCardsQuery.data ?? [])[0]?.coverImageUrl ?? null
                          : (meditationCardsQuery.data ?? [])[0]?.coverImageUrl ?? null;
                  const activityBackgroundImageUrl =
                    dietPlan?.coverImageUrl ??
                    ritual?.coverImageUrl ??
                    breath?.coverImageUrl ??
                    meditation?.coverImageUrl ??
                    fallbackBackgroundImageUrl ??
                    DEFAULT_COMPLETION_COVER_IMAGE;
                  const profileName = header?.displayName ?? "Kullanıcı";
                  const profileHandle = header?.handle ? `@${header.handle}` : "@user";
                  const profilePhoto = header?.photoUrl ?? "";
                  const interaction = activityInteractions[activity.id] ?? { sentEnergy: false, sentHealing: false };
                  const viewCount = Math.max(1, viewedActivityIds[activity.id] ? 1 : 0);
                  const energyCount = interaction.sentEnergy ? 1 : 0;
                  const healingCount = interaction.sentHealing ? 1 : 0;
                  const interactionCount = interaction.sentEnergy || interaction.sentHealing ? 1 : 0;
                  const collectiveEnergyPercent = Math.max(0, Math.min(100, Math.round((interactionCount / viewCount) * 100)));

              return (
                <Card key={`activity_${activity.id}`} className="overflow-hidden rounded-3xl border border-[#e5d7e8] bg-[#f8f3f7] p-0 shadow-sm">
                  <div className="relative px-6 pb-5 pt-6">
                    {activityBackgroundImageUrl ? (
                      <>
                        <div className="pointer-events-none absolute inset-0">
                          <img src={activityBackgroundImageUrl} alt={activityDisplayTitle} className="h-full w-full object-cover opacity-50" onError={(event) => applyCoverFallbackImage(event.currentTarget)} />
                        </div>
                        <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#f7eff4]/75 via-[#f7eff4]/70 to-[#f7eff4]/60" />
                      </>
                    ) : null}

                    <div className="relative z-10 mb-2 flex justify-end">
                      <div className="relative">
                        <Button
                          type="button"
                          size="icon"
                          variant="outline"
                          className="h-8 w-8 rounded-full border-white/90 bg-white text-slate-700 shadow-sm hover:bg-slate-100"
                          onClick={() => setMenuActivityId((prev) => (prev === activity.id ? null : activity.id))}
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>

                        {menuActivityId === activity.id ? (
                          <div className="absolute right-0 z-20 mt-2 w-36 rounded-xl border border-slate-200 bg-white p-1.5 shadow-lg">
                            <button
                              type="button"
                              disabled={deleteActivityMutation.isPending}
                              className="flex w-full items-center rounded-lg px-2 py-1.5 text-sm text-red-600 hover:bg-red-50 disabled:opacity-60"
                              onClick={() => {
                                void deleteActivityMutation.mutate(activity.id);
                                setMenuActivityId(null);
                              }}
                            >
                              <Trash2 className="mr-2 h-4 w-4" /> Sil
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <div className="relative mx-auto max-w-2xl text-center">
                      <h3 className="text-3xl font-black tracking-tight text-[#0f172a]">
                        {profileName}, {activityDisplayTitle} {activityMeta.completedLabel} tamamladı!
                      </h3>
                      <p className="mt-1 text-lg text-slate-600">{activity.subtitle}</p>

                      <div className="mt-5 grid grid-cols-3 gap-3">
                        <div className="rounded-2xl border border-white/70 bg-white/65 p-3">
                          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Süre</div>
                          <div className="mt-1 text-3xl font-black text-[#d96a23]">{Math.max(5, Math.round(activityDuration))}dk</div>
                        </div>
                        <div className="rounded-2xl border border-white/70 bg-white/65 p-3">
                          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Kolektif Enerji</div>
                          <div className="mt-1 text-3xl font-black text-[#d96a23]">{formatFocusPercent(collectiveEnergyPercent)}</div>
                        </div>
                        <div className="rounded-2xl border border-white/70 bg-white/65 p-3">
                          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Seri</div>
                          <div className="mt-1 text-3xl font-black text-[#d96a23]">{activity.streakDays} G.</div>
                        </div>
                      </div>

                      <div className="mt-5 flex justify-center">
                        <Button asChild type="button" className="rounded-xl bg-white text-slate-900 hover:bg-white/90">
                          <Link to={activityMeta.ctaHref}>Sen de Tamamla</Link>
                        </Button>
                      </div>

                      <div className="mt-4 flex flex-col items-center justify-center gap-2">
                        <Link to="/panel/profil" className="rounded-full">
                          <Avatar className="h-12 w-12 border border-white/70 shadow-sm">
                            <AvatarImage src={profilePhoto} alt={profileName} />
                            <AvatarFallback>{initials(profileName)}</AvatarFallback>
                          </Avatar>
                        </Link>
                        <Link to="/panel/profil" className="text-base font-semibold text-slate-900 hover:underline">
                          {profileName}
                        </Link>
                        <div className="text-xs text-slate-500">{profileHandle} • {formatRelative(activity.createdAt)}</div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3 border-t border-[#e7dce9] bg-white/80 px-4 py-3">
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Eye className="h-3.5 w-3.5" />
                      <span>{viewCount} görüntülenme</span>
                    </div>

                    <div className="h-2 overflow-hidden rounded-full bg-[#f2eaf5]">
                      <div className="h-full rounded-full bg-[#b879e4]" style={{ width: `${Math.max(2, collectiveEnergyPercent)}%` }} />
                    </div>

                    <div className="flex items-center justify-between gap-2">
                      <div className="text-xs text-slate-500">{energyCount} enerji • {healingCount} şifa</div>

                      <div className="ml-auto flex items-center gap-2">
                        <Button
                          type="button"
                          size="sm"
                          disabled={interaction.sentEnergy}
                          variant="outline"
                          className={ENERGY_BUTTON_CLASS}
                          onClick={() => toggleActivityInteraction(activity.id, "sentEnergy")}
                        >
                          <Zap className={cn("h-3.5 w-3.5", interaction.sentEnergy && "fill-current")} />
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          disabled={interaction.sentHealing}
                          variant="outline"
                          className={HEALING_BUTTON_CLASS}
                          onClick={() => toggleActivityInteraction(activity.id, "sentHealing")}
                        >
                          <Heart className={cn("h-3.5 w-3.5", interaction.sentHealing && "fill-current")} />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              );
                }

                const post = item.post;
                const isAffirmation = post.postType === "affirmation";
                const isOwnPost = post.authorUserId === user?.id;
                const isEditing = editingPostId === post.id;
                const firstMedia = post.media[0];
                const practiceCompletion = parsePracticeCompletionPost(post.body);
                const activityCompletion = parseActivityCompletionPost(post.body);

                if (isAffirmation) {
                  const isRose = post.affirmationStyle === "rose";
                  return (
                    <Card
                      key={post.id}
                      className={cn(
                        "overflow-hidden rounded-[30px] border p-0 shadow-md",
                        isRose
                          ? "border-[#f1d4df] bg-gradient-to-br from-[#ffeef5] via-[#f8e6ff] to-[#f0d8ff]"
                          : "border-[#c8b4ee] bg-gradient-to-br from-[#8f88ee] via-[#9b89f2] to-[#bb6df7]",
                      )}
                    >
                      <div className="p-6 text-center sm:p-8">
                        {isOwnPost ? (
                          <div className="mb-2 flex justify-end">
                            <div className="relative">
                              <Button
                                type="button"
                                size="icon"
                                variant="secondary"
                                className="h-8 w-8 rounded-full"
                                onClick={() => setMenuPostId((prev) => (prev === post.id ? null : post.id))}
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>

                              {menuPostId === post.id ? (
                                <div className="absolute right-0 z-20 mt-2 w-36 rounded-xl border border-slate-200 bg-white p-1.5 shadow-lg">
                                  <button
                                    type="button"
                                    className="flex w-full items-center rounded-lg px-2 py-1.5 text-sm hover:bg-slate-100"
                                    onClick={() => {
                                      setEditingPostId(post.id);
                                      setEditingBody(post.body);
                                      setMenuPostId(null);
                                    }}
                                  >
                                    <Pencil className="mr-2 h-4 w-4" /> Düzenle
                                  </button>
                                  <button
                                    type="button"
                                    disabled={deletePostMutation.isPending}
                                    className="flex w-full items-center rounded-lg px-2 py-1.5 text-sm text-red-600 hover:bg-red-50 disabled:opacity-60"
                                    onClick={() => {
                                      void deletePostMutation.mutate(post.id);
                                      setMenuPostId(null);
                                    }}
                                  >
                                    <Trash2 className="mr-2 h-4 w-4" /> Sil
                                  </button>
                                </div>
                              ) : null}
                            </div>
                          </div>
                        ) : null}
                        <div className={cn("text-xs font-bold uppercase tracking-[0.2em]", isRose ? "text-[#af5e7e]" : "text-[#ece3ff]")}>Olumlama Paylaşımı</div>
                        {post.body ? <p className={cn("mx-auto mt-5 max-w-2xl text-3xl font-medium italic leading-relaxed", isRose ? "text-[#3b2b48]" : "text-white")}>{`“${post.body}”`}</p> : null}

                        <div className="mt-6 flex flex-col items-center justify-center gap-2">
                          <Avatar className="h-16 w-16 border-2 border-white/70">
                            <AvatarImage src={post.authorPhotoUrl ?? ""} alt={post.authorName} />
                            <AvatarFallback>{initials(post.authorName)}</AvatarFallback>
                          </Avatar>
                          <div className={cn("text-sm font-semibold", isRose ? "text-[#5a3f60]" : "text-white")}>{post.authorName}</div>
                          <div className={cn("text-xs", isRose ? "text-[#7f698b]" : "text-white/85")}>@{post.authorHandle} • {formatRelative(post.createdAt)}</div>
                        </div>
                      </div>

                      {isEditing ? (
                        <div className="px-4 pb-2">
                          <Textarea
                            value={editingBody}
                            onChange={(event) => setEditingBody(event.target.value)}
                            maxLength={400}
                            className="min-h-[100px] resize-none rounded-xl border-white/30 bg-white/70 text-[#3d2f47]"
                          />
                          <div className="mt-2 flex justify-end gap-2">
                            <Button type="button" size="sm" variant="secondary" onClick={() => setEditingPostId(null)}>
                              Vazgeç
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              disabled={editPostMutation.isPending || (!editingBody.trim() && !post.media.length)}
                              onClick={() => void editPostMutation.mutate({ postId: post.id, body: editingBody })}
                            >
                              Kaydet
                            </Button>
                          </div>
                        </div>
                      ) : null}

                      {firstMedia ? (
                        <div className="aspect-video w-full bg-slate-100">
                          {firstMedia.type === "video" ? <video src={firstMedia.url} controls className="h-full w-full object-cover" /> : <img src={firstMedia.url} alt="Akış medyası" className="h-full w-full object-cover" />}
                        </div>
                      ) : null}

                      <div className={cn("space-y-3 border-t px-4 pb-4 pt-3", isRose ? "border-[#f0d8e6] bg-white/45" : "border-white/20 bg-[#7f66dd]/55")}>
                        <div className="flex items-center gap-2 text-xs">
                          <Eye className={cn("h-3.5 w-3.5", isRose ? "text-[#815a72]" : "text-white/90")} />
                          <span className={cn(isRose ? "text-[#6f4f63]" : "text-white/90")}>{post.viewCount} kişi gördü</span>
                          <span className={cn("ml-2 rounded-full px-2 py-0.5 font-semibold", isRose ? "bg-[#f7dde8] text-[#8f4f73]" : "bg-white/20 text-white")}>Kolektif Enerji %{post.collectiveEnergyPercent}</span>
                        </div>

                        <div className={cn("h-2 overflow-hidden rounded-full", isRose ? "bg-[#efdae5]" : "bg-white/20")}>
                          <div className={cn("h-full rounded-full", isRose ? "bg-[#d676a7]" : "bg-white")} style={{ width: `${Math.max(2, post.collectiveEnergyPercent)}%` }} />
                        </div>

                        <div className="flex items-center justify-between gap-2">
                          <div className={cn("text-xs", isRose ? "text-[#6f4f63]" : "text-white/90")}>{post.likeCount} enerji • {post.saveCount} şifa • {post.interactionCount} etkileşim</div>

                          <div className="ml-auto flex items-center gap-2">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className={ENERGY_BUTTON_CLASS}
                              onClick={() => void handlePostInteractionClick(post.id, "energy")}
                            >
                              <Zap className={cn("h-3.5 w-3.5", post.likedByMe && "fill-current")} />
                            </Button>

                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className={HEALING_BUTTON_CLASS}
                              onClick={() => void handlePostInteractionClick(post.id, "healing")}
                            >
                              <Heart className={cn("h-3.5 w-3.5", post.savedByMe && "fill-current")} />
                            </Button>
                          </div>
                        </div>

                        {renderPostComments(post, isRose ? "default" : "dark")}
                      </div>
                    </Card>
                  );
                }

                if (practiceCompletion || activityCompletion) {
                  const completionKind = practiceCompletion?.kind ?? activityCompletion?.kind ?? "aktivite";
                  const completionTitle = practiceCompletion?.title ?? activityCompletion?.title ?? post.body;
                  const isPractice = Boolean(practiceCompletion);
                  const sourceCards = isPractice
                    ? completionKind === "nefes"
                      ? (breathCardsQuery.data ?? [])
                      : (meditationCardsQuery.data ?? [])
                    : (ritualCardsQuery.data ?? []);
                  const fallbackCards = isPractice ? [] : [...(meditationCardsQuery.data ?? []), ...(breathCardsQuery.data ?? []), ...(dietCardsQuery.data ?? [])];
                  const completionVisual = resolveCompletionVisuals({
                    title: completionTitle,
                    primaryCards: sourceCards,
                    fallbackCards,
                  });
                  const completedLabel =
                    completionKind === "nefes"
                      ? "nefes çalışmasını"
                      : completionKind === "meditasyon"
                        ? "meditasyonunu"
                        : completionKind === "rituel"
                          ? "ritüelini"
                          : "aktivitesini";
                  const ctaHref =
                    completionKind === "nefes"
                      ? "/panel/nefes"
                      : completionKind === "meditasyon"
                        ? "/panel/meditasyon"
                        : completionKind === "rituel"
                          ? "/panel/rituel"
                          : "/panel/aktivite";
                  const streakDays = resolveCompletionStreakDays({
                    activities: activityCompletions,
                    authorUserId: post.authorUserId,
                    title: completionTitle,
                    kind: completionKind,
                    createdAt: post.createdAt,
                  });

                  return (
                    <Card key={post.id} className="overflow-hidden rounded-3xl border border-[#e5d7e8] bg-[#f8f3f7] p-0 shadow-sm">
                      <div className="relative px-6 pb-5 pt-6">
                        {completionVisual.coverImageUrl ? (
                          <>
                            <div className="pointer-events-none absolute inset-0">
                              <img src={completionVisual.coverImageUrl} alt={completionTitle} className="h-full w-full object-cover opacity-50" onError={(event) => applyCoverFallbackImage(event.currentTarget)} />
                            </div>
                            <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#f7eff4]/75 via-[#f7eff4]/70 to-[#f7eff4]/60" />
                          </>
                        ) : (
                          <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#f7eff4]/75 via-[#f7eff4]/70 to-[#f7eff4]/60" />
                        )}

                        {isOwnPost ? (
                          <div className="relative z-10 mb-2 flex justify-end">
                            <div className="relative">
                              <Button
                                type="button"
                                size="icon"
                                variant="outline"
                                className="h-8 w-8 rounded-full border-white/90 bg-white text-slate-700 shadow-sm hover:bg-slate-100"
                                onClick={() => setMenuPostId((prev) => (prev === post.id ? null : post.id))}
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>

                              {menuPostId === post.id ? (
                                <div className="absolute right-0 z-20 mt-2 w-36 rounded-xl border border-slate-200 bg-white p-1.5 shadow-lg">
                                  <button
                                    type="button"
                                    className="flex w-full items-center rounded-lg px-2 py-1.5 text-sm hover:bg-slate-100"
                                    onClick={() => {
                                      setEditingPostId(post.id);
                                      setEditingBody(post.body);
                                      setMenuPostId(null);
                                    }}
                                  >
                                    <Pencil className="mr-2 h-4 w-4" /> Düzenle
                                  </button>
                                  <button
                                    type="button"
                                    disabled={deletePostMutation.isPending}
                                    className="flex w-full items-center rounded-lg px-2 py-1.5 text-sm text-red-600 hover:bg-red-50 disabled:opacity-60"
                                    onClick={() => {
                                      void deletePostMutation.mutate(post.id);
                                      setMenuPostId(null);
                                    }}
                                  >
                                    <Trash2 className="mr-2 h-4 w-4" /> Sil
                                  </button>
                                </div>
                              ) : null}
                            </div>
                          </div>
                        ) : null}

                        <div className="relative mx-auto max-w-2xl text-center">
                          <h3 className="text-3xl font-black tracking-tight text-[#0f172a]">
                            {post.authorName}, {completionTitle} {completedLabel} tamamladı!
                          </h3>
                          <p className="mt-1 text-lg text-slate-600">Toplulukla paylaşıldı</p>

                          <div className="mt-5 grid grid-cols-3 gap-3">
                            <div className="rounded-2xl border border-white/70 bg-white/65 p-3">
                              <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Süre</div>
                              <div className="mt-1 text-3xl font-black text-[#d96a23]">{formatSessionDuration(completionVisual.durationMinutes)}</div>
                            </div>
                            <div className="rounded-2xl border border-white/70 bg-white/65 p-3">
                              <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Kolektif Enerji</div>
                              <div className="mt-1 text-3xl font-black text-[#d96a23]">{formatFocusPercent(post.collectiveEnergyPercent)}</div>
                            </div>
                            <div className="rounded-2xl border border-white/70 bg-white/65 p-3">
                              <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Seri</div>
                              <div className="mt-1 text-3xl font-black text-[#d96a23]">{streakDays} G.</div>
                            </div>
                          </div>

                          <div className="mt-5 flex justify-center">
                            <Button asChild type="button" className="rounded-xl bg-white text-slate-900 hover:bg-white/90">
                              <Link to={ctaHref}>Sen de Tamamla</Link>
                            </Button>
                          </div>

                          <div className="mt-4 flex flex-col items-center justify-center gap-2">
                            <Avatar className="h-12 w-12 border border-white/70 shadow-sm">
                              <AvatarImage src={post.authorPhotoUrl ?? ""} alt={post.authorName} />
                              <AvatarFallback>{initials(post.authorName)}</AvatarFallback>
                            </Avatar>
                            <div className="text-base font-semibold text-slate-900">{post.authorName}</div>
                            <div className="text-xs text-slate-500">@{post.authorHandle} • {formatRelative(post.createdAt)}</div>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-3 border-t border-[#e7dce9] bg-white/80 px-4 py-3">
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <Eye className="h-3.5 w-3.5" />
                          <span>{post.viewCount} görüntülenme</span>
                        </div>

                        <div className="h-2 overflow-hidden rounded-full bg-[#f2eaf5]">
                          <div className="h-full rounded-full bg-[#b879e4]" style={{ width: `${Math.max(2, post.collectiveEnergyPercent)}%` }} />
                        </div>

                        <div className="flex items-center justify-between gap-2">
                          <div className="text-xs text-slate-500">{post.likeCount} enerji • {post.saveCount} şifa • {post.interactionCount} etkileşim</div>

                          <div className="ml-auto flex items-center gap-2">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className={ENERGY_BUTTON_CLASS}
                              onClick={() => void handlePostInteractionClick(post.id, "energy")}
                            >
                              <Zap className={cn("h-3.5 w-3.5", post.likedByMe && "fill-current")} />
                            </Button>

                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className={HEALING_BUTTON_CLASS}
                              onClick={() => void handlePostInteractionClick(post.id, "healing")}
                            >
                              <Heart className={cn("h-3.5 w-3.5", post.savedByMe && "fill-current")} />
                            </Button>
                          </div>
                        </div>

                        {renderPostComments(post)}
                      </div>
                    </Card>
                  );
                }

                return (
                  <Card key={post.id} className="overflow-hidden rounded-2xl border border-[#e7d8e9] bg-white shadow-sm dark:bg-slate-900/40">
                    <div className="p-4">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex min-w-0 items-center gap-3">
                          <Avatar className="h-10 w-10 border border-slate-200">
                            <AvatarImage src={post.authorPhotoUrl ?? ""} alt={post.authorName} />
                            <AvatarFallback>{initials(post.authorName)}</AvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <div className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">{post.authorName}</div>
                            <div className="truncate text-xs text-slate-500">@{post.authorHandle} • {formatRelative(post.createdAt)}</div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          {post.location ? (
                            <div className="flex items-center gap-1 text-xs text-slate-500">
                              <MapPin className="h-3.5 w-3.5" /> {post.location}
                            </div>
                          ) : null}
                          {isOwnPost ? (
                            <div className="relative">
                              <Button
                                type="button"
                                size="icon"
                                variant="outline"
                                className="h-8 w-8 rounded-full"
                                onClick={() => setMenuPostId((prev) => (prev === post.id ? null : post.id))}
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>

                              {menuPostId === post.id ? (
                                <div className="absolute right-0 z-20 mt-2 w-36 rounded-xl border border-slate-200 bg-white p-1.5 shadow-lg">
                                  <button
                                    type="button"
                                    className="flex w-full items-center rounded-lg px-2 py-1.5 text-sm hover:bg-slate-100"
                                    onClick={() => {
                                      setEditingPostId(post.id);
                                      setEditingBody(post.body);
                                      setMenuPostId(null);
                                    }}
                                  >
                                    <Pencil className="mr-2 h-4 w-4" /> Düzenle
                                  </button>
                                  <button
                                    type="button"
                                    disabled={deletePostMutation.isPending}
                                    className="flex w-full items-center rounded-lg px-2 py-1.5 text-sm text-red-600 hover:bg-red-50 disabled:opacity-60"
                                    onClick={() => {
                                      void deletePostMutation.mutate(post.id);
                                      setMenuPostId(null);
                                    }}
                                  >
                                    <Trash2 className="mr-2 h-4 w-4" /> Sil
                                  </button>
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                      </div>

                      {isEditing ? (
                        <div className="mt-3 space-y-2">
                          <Textarea
                            value={editingBody}
                            onChange={(event) => setEditingBody(event.target.value)}
                            maxLength={400}
                            className="min-h-[100px] resize-none rounded-xl border-[#ddc6df] bg-[#f9f3fa] text-[#3d2f47]"
                          />
                          <div className="flex justify-end gap-2">
                            <Button type="button" size="sm" variant="outline" onClick={() => setEditingPostId(null)}>
                              Vazgeç
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              disabled={editPostMutation.isPending || (!editingBody.trim() && !post.media.length)}
                              onClick={() => void editPostMutation.mutate({ postId: post.id, body: editingBody })}
                            >
                              Kaydet
                            </Button>
                          </div>
                        </div>
                      ) : post.body ? <p className="mt-3 text-sm leading-relaxed text-slate-700 dark:text-slate-200">{post.body}</p> : null}
                    </div>

                    {firstMedia ? (
                      <div className="aspect-video w-full bg-slate-100">
                        {firstMedia.type === "video" ? <video src={firstMedia.url} controls className="h-full w-full object-cover" /> : <img src={firstMedia.url} alt="Akış medyası" className="h-full w-full object-cover" />}
                      </div>
                    ) : null}

                    <div className="space-y-3 border-t border-slate-100 p-3 dark:border-slate-800">
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <Eye className="h-3.5 w-3.5" />
                        <span>{post.viewCount} görüntülenme</span>
                        <span className="rounded-full bg-[#f1def5] px-2 py-0.5 font-semibold text-[#79458f]">Kolektif Enerji %{post.collectiveEnergyPercent}</span>
                      </div>

                      <div className="h-2 overflow-hidden rounded-full bg-[#f2eaf5]">
                        <div className="h-full rounded-full bg-[#b879e4]" style={{ width: `${Math.max(2, post.collectiveEnergyPercent)}%` }} />
                      </div>

                      <div className="flex items-center justify-between gap-2">
                        <div className="text-xs text-slate-500">{post.likeCount} enerji • {post.saveCount} şifa • {post.interactionCount} etkileşim</div>

                        <div className="ml-auto flex items-center gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className={ENERGY_BUTTON_CLASS}
                            onClick={async () => {
                              await toggleLikeSocialPost(post.id);
                              await commonInvalidate();
                            }}
                          >
                            <Zap className={cn("h-3.5 w-3.5", post.likedByMe && "fill-current")} />
                          </Button>

                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className={HEALING_BUTTON_CLASS}
                            onClick={async () => {
                              await toggleSaveSocialPost(post.id);
                              await commonInvalidate();
                            }}
                          >
                            <Heart className={cn("h-3.5 w-3.5", post.savedByMe && "fill-current")} />
                          </Button>
                        </div>
                      </div>

                      {renderPostComments(post)}
                    </div>
                  </Card>
                );
              })
            ) : (
              <div className="rounded-3xl border border-dashed border-primary/30 p-5 text-sm text-slate-600 dark:text-slate-300">Henüz paylaşım yok.</div>
            )}
          </div>

        </section>
      </div>
    </div>
  );
}
